import ChevronRightSvg from "@/src/assets/icons/icon-chevron-right-white.svg";
import DotGray4Svg from "@/src/assets/icons/icon-dot-gray4.svg";
import DotGray7Svg from "@/src/assets/icons/icon-dot-gray7.svg";
import AlertDialog from "@/src/components/atom/AlertDialog";
import Icon from "@/src/components/atom/Icon";
import Typo from "@/src/components/atom/Typo";
import BookMarkStatus from "@/src/components/molecule/BookMarkStatus";
import UnauthorizedDescription from "@/src/components/molecule/UnauthorizedDescription";
import NoRegisteredSignatureAlertDialog from "@/src/components/organism/NoRegisteredSignatureAlertDialog";
import SignatureRequestAlertDialog from "@/src/components/organism/SignatureRequestAlertDialog";
import EmailHistoryDialog from "@/src/components/organism/EmailHistoryDialog";
import SignatureSelectDialog from "@/src/components/organism/SignatureSelectDialog";
import ExporterMainLayout from "@/src/components/template/Layout/exporter/ExporterMainLayout";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";
import useAlert from "@/src/hooks/useAlert";
import useContentLoading from "@/src/hooks/useContentLoading";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import IMPORTER_PRIVATE_PATH from "@/src/routes/importer/path";
import { useAppSelector } from "@/src/store";
import { useGetSessionQuery } from "@/src/store/apis/auth";
import { useGetCommonCodeViaCodeNameQuery } from "@/src/store/apis/common";
import {
  useConfirmPoMutation,
  useConfirmScMutation,
  useGetContractDetailBookmarkQuery,
  useGetContractShipmentSummaryInfoQuery,
  useLazyGetContractDetailQuery,
  useLazyGetExporterContractSignatureHistoriesQuery,
  useLazyGetExporterPoHistoriesQuery,
  useLazyGetExporterScHistoriesQuery,
  useRejectPoMutation,
  useUpdateContractDetailBookmarkMutation,
  useUpdateContractDetailStatusMutation,
} from "@/src/store/apis/contracts/contractDetail";
import { ContractStatusType } from "@/src/store/apis/contracts/interface";
import { useGetExportersQuery } from "@/src/store/apis/corporate/company";
import { useLazyGetEmailShareHistoriesQuery } from "@/src/store/apis/emailShareHistories";
import { useGetExporterContractSignatureHistoriesQuery } from "@/src/store/apis/contracts/contractDetail";
import { isUndefined } from "@/src/utils/is";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ContractFilesTab from "./components/detail/ContractFilesTab";
import ContractInformationTab from "./components/detail/ContractInformationTab";
import ContractHistoryCard from "./components/detail/ContractHistoryCard";
import CancelContractAlertDialog from "./components/detail/dialog/CancelContractAlertDialog";
import EmailDialog from "./components/detail/dialog/EmailDialog";
import RedoAlertDialog from "./components/detail/dialog/RedoAlertDialog";
import RegisterSignDialog from "./components/detail/dialog/RegisterSignDialog";
import RejectReasonDialog from "./components/detail/dialog/RejectReasonDialog";
import ProcessingTab from "./components/detail/ProcessingTab";
import renderContractStatusBadge from "./utils/renderContractStatusBadge";
import { useGetBuyersQuery } from "@/src/store/apis/client/buyer";
import dayjs from "dayjs";
import { getContractCurrentVersion } from "@/src/utils/getContractCurrentVersion";
import { useCompanyLocalDate } from "@/src/hooks/useCompanyLocalDate";
import {
  determineContractFlow,
  determineContractStage,
  getHistoryDocumentType,
  isLeadRole as checkIsLeadRole,
  isCurrentLeadRole,
} from "@/src/utils/contractHistory";
import { useGetCreditStatusInfoQuery } from "@/src/store/apis/credit";
import { getContractNoticeType } from "@/src/utils/getContractNoticeType";
import {
  BottomButtonContainer,
  BottomContainer,
  ClientName,
  DeleteIcon,
  EditorContainer,
  FlexColumn,
  HeaderButtonContainer,
  HeaderContainer,
  NoDataContainer,
  PageTitleContainer,
  RedoIcon,
  StyledButton,
  StyledTabItem,
  Tabs,
} from "./components/detail/styles";
import EmailDropdown from "@/src/components/molecule/EmailDropdown";
import { SignatureFullscreenViewer } from "@/src/components/organism/SignatureFullscreenViewer";
import RejectReasonViewModal from "@/src/components/organism/RejectReasonViewModal";

enum DialogState {
  NULL,
  SEND_EMAIL,
  SENT_HISTORY,
  REGISTER_SIGN,
  REJECT_PO_REASON,
  CONTRACT_HISTORY,
  SIGNATURE_FULLSCREEN,
  VIEW_REJECT_REASON,
}

enum AlertDialogState {
  NULL,
  CANCEL,
  REDO,
  SIGN_PO,
  REJECT_PO,
  CONFIRM_REQUEST_SC,
  REGISTER_SIGN,
  SIGNATURE_REQUEST,
  SIGNATURE_SELECT_PO,
  SIGNATURE_SELECT_SC,
}

type TabType = "contractInformation" | "processing" | "files";

const TAB_LIST: {
  id: TabType;
  label: string;
}[] = [
  {
    id: "contractInformation",
    label: "common:exporterContractInformation",
  },
  {
    id: "processing",
    label: "common:processing",
  },
  {
    id: "files",
    label: "common:files",
  },
];

const ExporterContractDetailPage = () => {
  const { t } = useTranslation();
  const alert = useAlert();
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const subscription = useAppSelector((state) => state.subscription);

  // State
  const [tab, setTab] = useState<TabType>(() => {
    const tabParam = searchParams.get("selectTab");
    return (tabParam as TabType) || "contractInformation";
  });
  const [poVersion, setPoVersion] = useState<number>();
  const [scVersion, setScVersion] = useState<number>();
  const [processingTab, setProcessingTab] = useState<
    "booking" | "loading" | "shipment"
  >("booking");
  const { handleContentLoadingOff, handleContentLoadingOn } =
    useContentLoading();

  // API
  useGetCreditStatusInfoQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { mainCategoryList } = useGetCommonCodeViaCodeNameQuery(
    {
      codeName: "MAIN_CATEGORY",
    },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isError || isFetching || isUndefined(currentData);
        const isStable = !isUnstable;

        return {
          mainCategoryList: isStable ? (currentData ?? []) : [],
        };
      },
    },
  );
  const { currentData: buyers } = useGetBuyersQuery({ isActivated: true });

  const {
    userId,
    exporterUserType,
    userMainCategory,
    isUserFetching,
    exporterUserMainFieldType,
  } = useGetSessionQuery(undefined, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isError || isFetching || isUndefined(currentData);
      const isStable = !isUnstable;

      return {
        isUserFetching: isFetching,
        userId: currentData?.row.id,
        userMainCategory: isStable
          ? currentData.row.mainCategoryCodeItemNames
          : [],
        exporterUserType: currentData?.row.exporterUserType,
        exporterUserMainFieldType: currentData?.row.exporterUserMainFieldType,
      };
    },
  });

  const { hasSign, isTwoSign, signatureManagement } = useGetExportersQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isError }) => {
        const isUnstable = isError || isUndefined(currentData);
        const isStable = !isUnstable;
        return {
          hasSign: isStable
            ? currentData.signatureManagement.simpleExporterSignatureMedias
                ?.length > 0
            : false,
          isTwoSign: isStable
            ? currentData.signatureManagement.simpleExporterSignatureMedias
                ?.length === 2
            : false,
          signatureManagement: isStable
            ? currentData.signatureManagement.simpleExporterSignatureMedias
            : [],
        };
      },
    },
  );

  // Shipment summary info
  const { balanceOfContractV2, totalShippingWeightV2, percentOfShipment } =
    useGetContractShipmentSummaryInfoQuery(
      { id: Number(params.id) },
      {
        refetchOnMountOrArgChange: true,
        selectFromResult: ({ currentData, isError }) => {
          const isErrorAndUndefined = isError || currentData === undefined;

          return {
            balanceOfContractV2: !isErrorAndUndefined
              ? (currentData?.balanceOfContractV2 ?? "-")
              : "-",
            totalShippingWeightV2: !isErrorAndUndefined
              ? (currentData?.totalShippingWeightV2 ?? "-")
              : "-",
            percentOfShipment:
              !isErrorAndUndefined && currentData.percentOfShipment
                ? Number(currentData?.percentOfShipment)
                : 0,
          };
        },
      },
    );
  const [
    getContractDetail,
    {
      clientName,
      isContractError,
      contractWriterId,
      contractStatus,
      contractUpdatedAt,
      contractCreatedAt,
      contractLastEditorName,
      contractWriterName,
      scNo,
      poNo,
      isContractDetailFetching,
      isContractUnstable,
      isContractStable,
      contractMainCategory,
      contractMainCategoryForImporter,
      signatureStatus,
      buyerId,
      contractStatusInfo,
    },
  ] = useLazyGetContractDetailQuery({
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isUndefined(currentData) || isError || isFetching;
      const isStable = !isUnstable;

      return {
        buyerId: isStable ? currentData.buyerId : undefined,
        clientName: isStable ? currentData.buyerName : "",
        isContractUnstable: isUnstable,
        isContractStable: isStable,
        isContractError: isError,
        isContractDetailFetching: isFetching,
        contractWriterId: isStable ? currentData.contractWriterId : null,
        contractStatus: isStable ? currentData.contractStatus : "NOT_LINKED",
        contractUpdatedAt: isStable ? currentData.updatedAt : "",
        contractCreatedAt: isStable ? currentData.createdAt : "",
        contractLastEditorName: isStable
          ? (currentData.contractLastEditorName ?? "-")
          : "-",
        contractWriterName: isStable
          ? (currentData.contractWriterName ?? "-")
          : "-",
        scNo: isStable ? (currentData.scNo ?? "") : "",
        poNo: isStable ? (currentData.poNo ?? "") : "",
        scSimpleMedia: isStable ? currentData?.scSimpleMedia : null,
        contractMainCategory: isStable ? (currentData.mainCategory ?? "") : "",
        contractMainCategoryForImporter: isStable
          ? (currentData.mainCategoryForImporter ?? "")
          : "",
        signatureStatus: isStable ? currentData.signatureStatus : "DRAFT",
        contractStatusInfo: isStable ? currentData.contractStatusInfo : null,
      };
    },
  });
  const { isBookmarked } = useGetContractDetailBookmarkQuery(
    { id: Number(params.id) },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData }) => {
        return { isBookmarked: currentData?.isBookmarked };
      },
    },
  );
  const [editContractBookmark] = useUpdateContractDetailBookmarkMutation();
  const [contractStatusChange] = useUpdateContractDetailStatusMutation();
  const [
    getContractSharedHistories,
    {
      count,
      emailHistoryList,
      isEmailShareHistoriesFetching,
      isEmailShareHistoriesError,
    },
  ] = useLazyGetEmailShareHistoriesQuery({
    selectFromResult: ({ currentData, isFetching, isError }) => {
      const isUnstable = isUndefined(currentData) || isError || isFetching;

      return {
        count: !isUnstable ? currentData.count : 0,
        emailHistoryList: !isUnstable ? currentData.rows : undefined,
        isEmailShareHistoriesError: isError,
        isEmailShareHistoriesFetching: isFetching,
      };
    },
  });

  // Contract Signature Histories API
  const { data: signatureHistories } =
    useGetExporterContractSignatureHistoriesQuery(
      { id: Number(params.id) },
      {
        skip: !params.id,
      },
    );

  const [
    getContractSignatureHistories,
    {
      isOnlyReceivedPo,
      isPending,
      isExporterPoSigned,
      isImporterPoSigned,
      isExporterScSigned,
      isImporterScSigned,
      hasLinkedBefore,
      isSentSc,
      previousSignature,
      currentSignature,
    },
  ] = useLazyGetExporterContractSignatureHistoriesQuery({
    selectFromResult: ({ currentData }) => ({
      previousSignature: currentData?.rows[1],
      currentSignature: currentData?.rows[0],
      isOnlyReceivedPo:
        currentData?.rows[0].signatureStatus === "PENDING" ||
        (currentData?.rows[0].signatureStatus === "SENT" &&
          currentData?.rows[0].isImporterSignedPO),
      isPending: currentData?.rows[0].signatureStatus === "PENDING",
      isExporterPoSigned: !!currentData?.rows[0].isExporterSignedPO,
      isImporterPoSigned: !!currentData?.rows[0].isImporterSignedPO,
      isExporterScSigned: !!currentData?.rows[0].isExporterSignedSC,
      isImporterScSigned: !!currentData?.rows[0].isImporterSignedSC,
      hasLinkedBefore: currentData?.rows.some(
        ({
          isExporterSignedSC,
          isImporterSignedSC,
          isExporterSignedPO,
          isImporterSignedPO,
        }) =>
          isExporterSignedSC &&
          isImporterSignedSC &&
          isExporterSignedPO &&
          isImporterSignedPO,
      ),
      isSentSc: currentData?.rows[0].isExporterSignedSC,
    }),
  });

  const [getExporterPoHistories] = useLazyGetExporterPoHistoriesQuery();
  const [getExporterScHistories, { scHistories, isZeroSc }] =
    useLazyGetExporterScHistoriesQuery({
      selectFromResult: ({ currentData, isError }) => {
        return {
          scHistories: currentData?.rows ?? [],
          isZeroSc: isError,
        };
      },
    });

  const [confirmSc] = useConfirmScMutation();
  const [confirmPo] = useConfirmPoMutation();
  const [rejectPo] = useRejectPoMutation();

  // State
  const [filesTab, setFilesTab] = useState<"list" | "gallery">("list");
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.NULL);
  const [alertDialogState, setAlertDialogState] = useState<AlertDialogState>(
    AlertDialogState.NULL,
  );
  const [selectedSignatureId, setSelectedSignatureId] = useState<number | null>(
    null,
  );
  const [showRegisterSignDialog, setShowRegisterSignDialog] = useState(false);
  const [signatureType, setSignatureType] = useState<"PO" | "SC" | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const contractCreatedDate = useCompanyLocalDate({
    date: contractCreatedAt,
    format: DATE_FORMAT_STRINGS.YYYY_MM_DD_HH_mm,
  });

  const contractUpdatedDate = useCompanyLocalDate({
    date: contractUpdatedAt,
    format: DATE_FORMAT_STRINGS.YYYY_MM_DD_HH_mm,
  });

  const defaultSignature = signatureManagement?.find((item) => item.isDefault);
  const defaultSignatureMediaId = defaultSignature?.signatureSimpleMedia.id;

  const numberOfUsers =
    (subscription.subscriptionCredit?.numberOfMember as number) -
      (subscription.subscriptionCredit?.memberNumberCredit as number) || 0;

  const isCreateDisabled =
    exporterUserMainFieldType === "BUYER" ||
    !subscription.isCompanySubscription ||
    (subscription?.subscriptionCredit?.numberOfMember || 0) < numberOfUsers;

  const mainCategoryItemName = mainCategoryList.find(
    (item) =>
      item.codeItemName ===
        contractMainCategory.toUpperCase().replace(/-/g, "_") ||
      item.codeItemName ===
        contractMainCategoryForImporter.toUpperCase().replace(/-/g, "_"),
  )?.codeItemName;

  // 매니저,중간관리자 카테고리 비교
  const isMainCategoryAuthorized =
    exporterUserType === "MANAGER" || exporterUserType === "MIDDLE_MANAGER"
      ? userMainCategory.includes(mainCategoryItemName as string)
      : true;

  // 권한이 없는 유저경우
  const isMainCategoryUnauthorized = !isMainCategoryAuthorized;

  /**
   * 컨펌요청 disabled
   * - 모든 서명 완료, SC미생성
   * - 미연결 되어있는 거래처
   * - 버전업 이후 SC생성전
   */
  const _requestConfirmScDisabled = useMemo(() => {
    if (signatureStatus === "LINKED" || isZeroSc) {
      return true;
    }

    if (
      signatureStatus === "APPROVED" &&
      getContractCurrentVersion(scHistories).currentVersion >= 2 &&
      getContractCurrentVersion(scHistories).currentVersionHistoryLength === 1
    ) {
      return true;
    }

    if (
      contractStatus === "NOT_LINKED" &&
      buyers?.data.find(
        ({ buyerListQueryResultDto }) => buyerId === buyerListQueryResultDto.id,
      )?.buyerListQueryResultDto.linkedStatus !== "COMPLETED"
    ) {
      return true;
    }
  }, [
    signatureStatus,
    isZeroSc,
    scHistories,
    contractStatus,
    buyers?.data,
    buyerId,
  ]);

  /**
   * 연결되어있는 SC수정버튼 disable
   */
  const isLinkedScEditDisabled = useMemo(() => {
    if (currentSignature?.signatureStatus === "SENT") {
      return true;
    }

    if (contractStatus === "PROCESSING") {
      if (
        currentSignature?.signatureStatus === "APPROVED" &&
        !currentSignature?.isExporterSignedSC
      ) {
        return false;
      }

      if (!hasLinkedBefore && !previousSignature?.isExporterSignedSC) {
        return true;
      }
    }
  }, [
    contractStatus,
    currentSignature?.isExporterSignedSC,
    currentSignature?.signatureStatus,
    hasLinkedBefore,
    previousSignature?.isExporterSignedSC,
  ]);

  /**
   * 수정권한 (유저상태)
   * - 기업관리자, 중간관리자, 작성자
   * - 일반 매니저는 메인카테고리가 포함된 사람
   */
  const isEditAuth =
    !isContractError &&
    (userId === contractWriterId || isMainCategoryAuthorized);

  /**
   * - 수정권한이 없는유저
   * - 거래처가 연결되어있지 않는 경우
   * - 서명 요청상태 Request
   * - 버전업 이후 서명 요청상태 PO를 Pending 한 경우
   * - 버전업 이전 SC를 요청, 거래처와 연결이 되어있지 않고, 서명이 모두 완료상태
   */
  const isDisabledEdit = useMemo(() => {
    if (
      !isEditAuth ||
      isLinkedScEditDisabled ||
      signatureStatus === "REQUESTED"
    ) {
      return true;
    }

    if (
      hasLinkedBefore &&
      signatureStatus === "PENDING" &&
      previousSignature?.isImporterSignedPO &&
      !previousSignature?.isExporterSignedPO
    ) {
      return true;
    }

    return (
      !!isSentSc &&
      contractStatus !== "NOT_LINKED" &&
      signatureStatus !== "LINKED"
    );
  }, [
    isEditAuth,
    isLinkedScEditDisabled,
    signatureStatus,
    hasLinkedBefore,
    previousSignature?.isImporterSignedPO,
    previousSignature?.isExporterSignedPO,
    isSentSc,
    contractStatus,
  ]);

  const handleBookmarkClick = async () => {
    const bookmark = !isBookmarked;

    try {
      const bookmarkParams: { id: number; isBookmarked: boolean } = {
        id: Number(params.id),
        isBookmarked: bookmark,
      };

      await editContractBookmark(bookmarkParams).unwrap();
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const handleContractStatusChangeClick = async (
    type: "COMPLETE" | "PROCESSING",
  ) => {
    try {
      const contractParams = {
        contractStatus: type,
        contractId: Number(params.id),
      };

      await contractStatusChange(contractParams).unwrap();

      if (type === "COMPLETE") {
        alert.showAlert({
          type: "success",
          message: t("contract:exporter.detail.alert.completeAlert"),
        });
      }

      if (type === "PROCESSING") {
        alert.showAlert({
          type: "success",
          message: t("contract:exporter.detail.alert.redoAlert"),
        });
      }
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const handleDocument = async (data: {
    type: "confirmPo" | "confirmSc" | "rejectPo";
    rejectReason?: string;
    signatureMediaId?: number;
  }) => {
    try {
      handleContentLoadingOn();
      const id = Number(params.id);
      switch (data.type) {
        case "confirmPo":
          if (data.signatureMediaId) {
            await confirmPo({
              id,
              signatureMediaId: data.signatureMediaId,
            }).unwrap();
          }
          alert.showAlert({
            type: "success",
            message: t(
              "contract:importer.detail.alert.signatureCompletedAlert",
            ),
          });
          break;
        case "confirmSc":
          if (data.signatureMediaId) {
            await confirmSc({
              id,
              signatureMediaId: data.signatureMediaId,
            }).unwrap();
          }
          alert.showAlert({
            type: "success",
            message: t(
              "contract:exporter.detail.alert.signatureCompletedAlert",
            ),
          });
          break;
        case "rejectPo":
          await rejectPo({ id, rejectReason: data.rejectReason }).unwrap();
          alert.showAlert({
            type: "success",
            message: t("contract:exporter.detail.alert.rejectCompletedAlert"),
          });
          break;
      }
      await getContractDetail({
        id: Number(params.id),
      });
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    } finally {
      setDialogState(DialogState.NULL);
      setAlertDialogState(AlertDialogState.NULL);
      handleContentLoadingOff();
    }
  };

  const handleTabChange = (newTab: TabType) => {
    setTab(newTab);
    setSearchParams({ selectTab: newTab }, { replace: true });
  };

  const renderDialogs = () => {
    if (dialogState === DialogState.SEND_EMAIL) {
      return (
        <EmailDialog
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
        />
      );
    }
    if (dialogState === DialogState.SENT_HISTORY) {
      return (
        <EmailHistoryDialog
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
          isFetching={isEmailShareHistoriesFetching}
          isError={isEmailShareHistoriesError}
          emailHistoryList={emailHistoryList}
          totalCount={count}
          fetch={getContractSharedHistories}
          emailShareDomain={"CONTRACT"}
          emailShareDomainId={Number(params.id)}
        />
      );
    }
    if (dialogState === DialogState.REJECT_PO_REASON) {
      return (
        <RejectReasonDialog
          onClose={() => setDialogState(DialogState.NULL)}
          onSubmit={async (rejectReason) => {
            await handleDocument({ type: "rejectPo", rejectReason });
          }}
        />
      );
    }
    // 서명 등록 다이얼로그는 별도로 처리됨
    if (dialogState === DialogState.CONTRACT_HISTORY) {
      // 플로우, 단계, Lead 여부 계산
      const flow = determineContractFlow(
        signatureHistories?.rows,
        "EXPORTER_LEAD",
      );
      const _currentStage = determineContractStage(
        signatureHistories?.rows,
        flow,
      );
      const isLead = checkIsLeadRole(signatureHistories?.rows, "EXPORTER");

      // Format signature histories for display
      const formatSignatureHistory = (history: any) => {
        // 문서 타입 판단 - 유틸리티 함수 사용
        const docType = getHistoryDocumentType(history, flow);

        const getTitle = () => {
          // 반려 상태를 먼저 체크 (PENDING + rejectReason)
          if (history.signatureStatus === "PENDING" && history.rejectReason) {
            if (docType === "SC") {
              return t("contract:history.status.scRejected");
            } else if (docType === "PO") {
              return t("contract:history.status.poRejected");
            }
            return t("contract:history.status.scRejected"); // Default to SC for exporter
          }

          switch (history.signatureStatus) {
            case "DRAFT":
              if (docType === "SC") {
                return t("contract:history.status.scDraftCreated");
              } else if (docType === "PO") {
                return t("contract:history.status.poDraftCreated");
              }
              return t("contract:history.status.draftCreated");
            case "SENT":
              if (docType === "SC") {
                return t("contract:history.status.scSignatureRequested");
              } else if (docType === "PO") {
                return t("contract:history.status.poSignatureRequested");
              }
              return t("contract:history.status.signatureRequested");
            case "PENDING":
              if (docType === "SC") {
                return t("contract:history.status.scSignaturePending");
              } else if (docType === "PO") {
                return t("contract:history.status.poSignaturePending");
              }
              return t("contract:history.status.signaturePending");
            case "APPROVED":
              if (docType === "SC") {
                return t("contract:history.status.scSignatureCompleted");
              } else if (docType === "PO") {
                return t("contract:history.status.poSignatureCompleted");
              }
              return t("contract:history.status.signatureCompleted");
            case "REQUESTED":
              if (docType === "SC") {
                return t("contract:history.status.scSignatureRequested");
              } else if (docType === "PO") {
                return t("contract:history.status.poSignatureRequested");
              }
              return t("contract:history.status.signatureRequested");
            case "LINKED":
              return t("contract:history.status.contractLinked");
            case "REJECTED":
              if (docType === "SC") {
                return t("contract:history.status.scRejected");
              } else if (docType === "PO") {
                return t("contract:history.status.poRejected");
              }
              return t("contract:history.status.scRejected"); // Default to SC for exporter
            default:
              return t("contract:history.status.statusChanged");
          }
        };

        const getStatus = () => {
          if (history.rejectReason || history.signatureStatus === "REJECTED") {
            return "rejected" as const;
          }
          return "completed" as const;
        };

        return {
          id: history.id,
          status: getStatus(),
          title: getTitle(),
          user: history.editorName || "-",
          timestamp: dayjs(history.updatedAt).format("YY-MM-DD HH:mm"),
          rejectionReason: history.rejectReason,
          updatedAt: history.updatedAt, // 정렬을 위해 원본 날짜 보존
        };
      };

      const historyItems =
        signatureHistories?.rows
          ?.map(formatSignatureHistory)
          ?.filter((item, index) => {
            // Follow 역할이고 DRAFT 상태인 경우 필터링
            const originalHistory = signatureHistories?.rows?.[index];

            // Follow이고 DRAFT 상태인 경우 필터링
            if (!isLead && originalHistory?.signatureStatus === "DRAFT") {
              // 수출자 Follow인 경우 PO 초안은 보지 않음
              // PO 문서인지 확인 (importerPOSignMedia가 있고 exporterScSignMedia가 없는 경우)
              const isPODraft =
                originalHistory.importerPOSignMedia &&
                !originalHistory.exporterScSignMedia;
              if (isPODraft) {
                return false;
              }
            }
            return true;
          })
          ?.sort((a, b) => {
            // 원본 날짜를 비교하여 최신 것부터 정렬 (내림차순)
            const dateA = dayjs(a.updatedAt);
            const dateB = dayjs(b.updatedAt);
            return dateA.isBefore(dateB) ? 1 : dateA.isAfter(dateB) ? -1 : 0;
          }) || [];

      // Add contract completion status if contract is complete
      if (contractStatus === "COMPLETE" && historyItems.length > 0) {
        historyItems.unshift({
          id: 0,
          status: "completed" as const,
          title: t("contract:history.status.contractCompleted"),
          user: contractLastEditorName,
          timestamp: dayjs(contractUpdatedAt).format("YY-MM-DD HH:mm"),
          updatedAt: contractUpdatedAt,
        });
      }

      return (
        <ContractHistoryCard
          historyItems={historyItems}
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
          modalTitle={t("contract:history.modalTitle")}
        />
      );
    }
    // 서명 전체화면
    if (
      dialogState === DialogState.SIGNATURE_FULLSCREEN &&
      pdfUrl &&
      signatureType
    ) {
      const progressInfo = getProgressInfo();
      return (
        <SignatureFullscreenViewer
          isOpen={true}
          onClose={() => {
            setDialogState(DialogState.NULL);
            setPdfUrl(null);
            setSelectedSignatureId(null);
            setSignatureType(null);
          }}
          pdfUrl={pdfUrl}
          contractNo={signatureType === "PO" ? poNo || "" : scNo || ""}
          userType="EXPORTER"
          onSign={async () => {
            // 서명이 선택되지 않았으면 서명 선택 프로세스 시작
            if (!selectedSignatureId) {
              if (!hasSign) {
                setAlertDialogState(AlertDialogState.REGISTER_SIGN);
              } else if (isTwoSign) {
                setAlertDialogState(
                  signatureType === "PO"
                    ? AlertDialogState.SIGNATURE_SELECT_PO
                    : AlertDialogState.SIGNATURE_SELECT_SC,
                );
              } else {
                // 서명이 1개인 경우 바로 서명 처리
                if (defaultSignatureMediaId) {
                  await handleDocument({
                    type: signatureType === "PO" ? "confirmPo" : "confirmSc",
                    signatureMediaId: defaultSignatureMediaId,
                  });
                }
              }
            } else {
              await handleDocument({
                type: signatureType === "PO" ? "confirmPo" : "confirmSc",
                signatureMediaId: selectedSignatureId,
              });
            }
          }}
          onReject={async (reason) => {
            if (signatureType === "PO" && reason) {
              await handleDocument({ type: "rejectPo", rejectReason: reason });
            }
          }}
          currentStep={progressInfo.currentStep}
          nextStep={progressInfo.nextStep}
        />
      );
    }
    // 반려 사유 보기 모달
    if (dialogState === DialogState.VIEW_REJECT_REASON) {
      // 반려 사유 확인 버튼 표시 여부 결정
      // SC 반려: 수출자가 확인해야 함 (수출자 페이지에서 버튼 표시)
      // PO 반려: 수입자가 확인해야 함 (수출자 페이지에서 버튼 숨김)
      let hideEditButton = false;

      if (contractNoticeType?.includes("PO_REJECTED")) {
        hideEditButton = true; // PO 반려는 수입자가 확인
      }

      return (
        <RejectReasonViewModal
          isOpen={true}
          onClose={() => setDialogState(DialogState.NULL)}
          rejectReason={currentSignature?.rejectReason}
          title={t("contract:reject.viewTitle")}
          contractId={Number(params.id)}
          hideEditButton={hideEditButton}
        />
      );
    }
  };

  const getProgressInfo = () => {
    if (signatureType === "PO") {
      return {
        currentStep: t("contract:status.notice.poSignaturePending.title"),
        nextStep: t("contract:status.notice.contractComplete.title"),
      };
    } else {
      return {
        currentStep: t("contract:status.notice.scSignaturePending.title"),
        nextStep: t("contract:status.notice.contractComplete.title"),
      };
    }
  };

  const renderAlertDialogs = () => {
    const closeAlertDialog = () => setAlertDialogState(AlertDialogState.NULL);
    // 계약 완료
    if (alertDialogState === AlertDialogState.CANCEL) {
      return (
        <CancelContractAlertDialog
          open
          onOpenChange={() => closeAlertDialog()}
          onOk={() => {
            closeAlertDialog();
            handleContractStatusChangeClick("COMPLETE");
          }}
        />
      );
    }
    // 계약 되돌리기
    if (alertDialogState === AlertDialogState.REDO) {
      return (
        <RedoAlertDialog
          open
          onOpenChange={() => closeAlertDialog()}
          onOk={() => {
            closeAlertDialog();
            handleContractStatusChangeClick("PROCESSING");
          }}
        />
      );
    }
    // PO서명 (1개일 경우)
    if (alertDialogState === AlertDialogState.SIGN_PO) {
      // PDF URL 가져오기
      (async () => {
        try {
          const contractId = Number(params.id);
          const poHistories = await getExporterPoHistories({
            contractId,
          }).unwrap();
          const pdfUrl = poHistories?.rows?.[0]?.mediaFileUrl;
          if (pdfUrl) {
            setPdfUrl(pdfUrl);
            if (defaultSignatureMediaId) {
              setSelectedSignatureId(defaultSignatureMediaId);
            }
            setSignatureType("PO");
            setDialogState(DialogState.SIGNATURE_FULLSCREEN);
          }
        } catch (error) {
          console.error("Failed to fetch PO PDF URL:", error);
        }
      })();
      closeAlertDialog();
      return null;
    }
    // PO서명 서명선택 (서명 2개일 경우)
    if (alertDialogState === AlertDialogState.SIGNATURE_SELECT_PO) {
      return (
        <SignatureSelectDialog
          open
          onSave={async (selectSignatureMediaId) => {
            try {
              // 서명 선택 완료 시 바로 서명 처리
              setSelectedSignatureId(selectSignatureMediaId);
              closeAlertDialog();

              await handleDocument({
                type: "confirmPo",
                signatureMediaId: selectSignatureMediaId,
              });
              // 풀스크린도 함께 닫기
              setDialogState(DialogState.NULL);
            } catch (error) {
              console.error("Failed to process signature:", error);
            }
          }}
          onOpenChange={closeAlertDialog}
          onRegisterClick={() => {
            // 현재 알림 다이얼로그만 닫고 서명 등록 다이얼로그 열기
            setAlertDialogState(AlertDialogState.NULL);
            setShowRegisterSignDialog(true);
          }}
        />
      );
    }
    // PO반려
    if (alertDialogState === AlertDialogState.REJECT_PO) {
      return (
        <AlertDialog
          open
          onOpenChange={closeAlertDialog}
          title={t("contract:exporter.detail.alertDialog.rejectContractTitle")}
          description={t(
            "contract:exporter.detail.alertDialog.rejectContractDescription",
          )}
          onOk={async () => await handleDocument({ type: "rejectPo" })}
          onCancel={closeAlertDialog}
          cancelText={t("contract:exporter.detail.button.cancelButton")}
          okText={t("contract:exporter.detail.button.okButton")}
        />
      );
    }
    // SC컨펌요청 (서명 1개일 경우)
    if (alertDialogState === AlertDialogState.CONFIRM_REQUEST_SC) {
      if (defaultSignatureMediaId) {
        setSelectedSignatureId(defaultSignatureMediaId);
      }
      setSignatureType("SC");
      setDialogState(DialogState.SIGNATURE_FULLSCREEN);
      closeAlertDialog();
      return null;
    }
    // SC컨펌요청 (서명 2개일 경우)
    if (alertDialogState === AlertDialogState.SIGNATURE_SELECT_SC) {
      return (
        <SignatureSelectDialog
          open
          onSave={async (selectSignatureMediaId) => {
            try {
              // 서명 선택 완료 시 바로 서명 처리
              setSelectedSignatureId(selectSignatureMediaId);
              closeAlertDialog();

              await handleDocument({
                type: "confirmSc",
                signatureMediaId: selectSignatureMediaId,
              });
              // 풀스크린도 함께 닫기
              setDialogState(DialogState.NULL);
            } catch (error) {
              console.error("Failed to process signature:", error);
            }
          }}
          onOpenChange={closeAlertDialog}
          onRegisterClick={() => {
            // 현재 알림 다이얼로그만 닫고 서명 등록 다이얼로그 열기
            setAlertDialogState(AlertDialogState.NULL);
            setShowRegisterSignDialog(true);
          }}
        />
      );
    }
    // 서명등록
    if (alertDialogState === AlertDialogState.REGISTER_SIGN) {
      return (
        <NoRegisteredSignatureAlertDialog
          open
          onOpenChange={closeAlertDialog}
          onOk={() => {
            // 현재 알림 다이얼로그만 닫고 서명 등록 다이얼로그 열기
            setAlertDialogState(AlertDialogState.NULL);
            setShowRegisterSignDialog(true);
          }}
          onCancel={closeAlertDialog}
        />
      );
    }
    // 서명등록 요청
    if (alertDialogState === AlertDialogState.SIGNATURE_REQUEST) {
      return (
        <SignatureRequestAlertDialog
          open
          onOpenChange={closeAlertDialog}
          onCancel={closeAlertDialog}
        />
      );
    }
  };

  const renderUnauthorizedDescription = () => {
    if (isContractDetailFetching || isUserFetching) {
      return;
    }

    if (isContractUnstable || isMainCategoryUnauthorized) {
      return (
        <NoDataContainer>
          <UnauthorizedDescription />
        </NoDataContainer>
      );
    }
  };

  const renderFooter = () => {
    switch (tab) {
      case "contractInformation":
        return (
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            {contractStatus === "COMPLETE" ? (
              <StyledButton
                buttonColor="blue"
                buttonGrade="secondary"
                onClick={() => setAlertDialogState(AlertDialogState.REDO)}
                disabled={!isEditAuth}
              >
                <RedoIcon disabled={!isEditAuth} />
                {t("contract:exporter.detail.bottomBottom.contractRedoButton")}
              </StyledButton>
            ) : (
              <StyledButton
                buttonColor="red"
                buttonGrade="secondary"
                onClick={() => setAlertDialogState(AlertDialogState.CANCEL)}
                disabled={!isEditAuth || isCreateDisabled}
              >
                <DeleteIcon disabled={!isEditAuth || isCreateDisabled} />
                {t(
                  "contract:exporter.detail.bottomBottom.contractCancelButton",
                )}
              </StyledButton>
            )}

            <StyledButton
              onClick={() =>
                navigate(
                  `${EXPORTER_PRIVATE_PATH.BOOKING_ADD}?contractId=${Number(
                    params.id,
                  )}`,
                )
              }
              disabled={
                contractStatus === "COMPLETE" || !isEditAuth || isCreateDisabled
              }
            >
              {t("contract:exporter.detail.bottomBottom.createBookingButton")}
              <Icon iconSrc={ChevronRightSvg} iconSize={16} />
            </StyledButton>
          </div>
        );
    }
  };

  // 계약 상태에 따른 알림 타입 계산
  const contractNoticeType = useMemo(() => {
    // Lead/Follow 판단: 현재 단계 기준으로 판단
    const isLeadRole = isCurrentLeadRole(signatureHistories?.rows, "EXPORTER");

    // 거래처 연결 여부 확인 (buyer의 linkedStatus가 COMPLETED인지)
    const buyer = buyers?.data.find(
      ({ buyerListQueryResultDto }) => buyerId === buyerListQueryResultDto.id,
    );
    const isLinkedBuyer =
      buyer?.buyerListQueryResultDto.linkedStatus === "COMPLETED";

    const noticeType = getContractNoticeType({
      contractStatus,
      signatureStatus: currentSignature?.signatureStatus || signatureStatus,
      poNo,
      scNo,
      isOnlyReceivedPo,
      isExporterPoSigned,
      isImporterPoSigned,
      isExporterScSigned,
      isImporterScSigned,
      isPending,
      isSentSc,
      hasLinkedBefore,
      userRole: "EXPORTER",
      isLeadRole,
      // 반려 상태 판단 (백엔드 기준):
      // - signatureStatus가 'PENDING'이고
      // - rejectReason이 null이 아니고 빈 문자열이 아닌 경우
      isRejected:
        currentSignature?.signatureStatus === "PENDING" &&
        currentSignature?.rejectReason !== null &&
        currentSignature?.rejectReason !== "",
      isLinkedBuyer,
      contractStatusInfo,
    });
    return noticeType;
  }, [
    contractStatus,
    signatureStatus,
    poNo,
    scNo,
    isOnlyReceivedPo,
    isExporterPoSigned,
    isImporterPoSigned,
    isExporterScSigned,
    isImporterScSigned,
    isPending,
    isSentSc,
    hasLinkedBefore,
    currentSignature?.rejectReason,
    currentSignature?.signatureStatus,
    signatureHistories,
    buyers?.data,
    buyerId,
    contractStatusInfo,
  ]);

  // 알림 액션 핸들러
  const handleContractNoticeAction = useCallback(
    (actionType?: string) => {
      // VIEW_REJECTION_REASON 액션이 직접 전달된 경우
      if (actionType === "VIEW_REJECTION_REASON") {
        setDialogState(DialogState.VIEW_REJECT_REASON);
        return;
      }

      // 기존 contractNoticeType 기반 처리 (하위 호환성)
      if (!contractNoticeType) return;

      switch (contractNoticeType) {
        case "EX_LEAD_SC_DRAFT":
          // 거래처 연동은 ContractInformationTab의 LINK_PARTNER 액션으로 처리
          // ContractInformationTab에서 초대코드 발송 모달을 열도록 함
          break;

        case "EX_LEAD_SC_SIGNATURE_REQUIRED":
        case "EX_FOLLOW_SC_SIGNATURE_REQUIRED":
        case "IM_LEAD_SC_SIGNATURE_REQUIRED":
          // SC 서명 요청 버튼 클릭 시 무조건 Edit 페이지로 이동
          navigate(`${EXPORTER_PRIVATE_PATH.CONTRACT_EDIT_V2}/${params.id}`);
          break;

        case "EX_LEAD_SC_REJECTED":
        case "IM_LEAD_SC_REJECTED":
        case "EX_FOLLOW_SC_REJECTED":
          // SC 반려 사유 보기
          setDialogState(DialogState.VIEW_REJECT_REASON);
          break;

        case "EX_LEAD_SC_SIGNED":
          // PO 생성 요청 - 이메일 발송 다이얼로그 표시
          setDialogState(DialogState.SEND_EMAIL);
          break;

        case "EX_LEAD_PO_REJECTED":
        case "IM_LEAD_PO_REJECTED":
        case "EX_FOLLOW_PO_REJECTED":
        case "IM_FOLLOW_PO_REJECTED":
          // PO 반려 사유 보기
          setDialogState(DialogState.VIEW_REJECT_REASON);
          break;

        case "EX_LEAD_PO_SIGNATURE_REQUIRED":
        case "EX_FOLLOW_PO_SIGNATURE_REQUIRED":
        case "IM_LEAD_PO_SIGNATURE_REQUIRED":
        case "IM_FOLLOW_PO_SIGNATURE_REQUIRED":
          // PO 계약서 보기 - 풀스크린 미리보기로 바로 이동
          (async () => {
            try {
              const contractId = Number(params.id);
              const poHistories = await getExporterPoHistories({
                contractId,
              }).unwrap();
              const pdfUrl = poHistories?.rows?.[0]?.mediaFileUrl;
              if (pdfUrl) {
                setPdfUrl(pdfUrl);
                setSelectedSignatureId(null); // 서명 선택은 나중에
                setSignatureType("PO");
                setDialogState(DialogState.SIGNATURE_FULLSCREEN);
              }
            } catch (error) {
              console.error("Failed to fetch PO PDF URL:", error);
            }
          })();
          break;

        case "EX_LEAD_CONTRACT_COMPLETE":
        case "EX_FOLLOW_CONTRACT_COMPLETE":
        case "IM_LEAD_CONTRACT_COMPLETE":
        case "IM_FOLLOW_CONTRACT_COMPLETE":
          // 계약서 보기 - 모달로 표시
          // ContractInformationTab의 setIsContractViewerOpen을 사용할 수 없으므로
          // 여기서는 아무 동작도 하지 않고, ContractProgressBar에 별도 핸들러 전달
          break;

        case "IM_LEAD_PO_DRAFT":
        case "EX_FOLLOW_PO_RECEIVED":
          // PO 확인 필요
          // TODO: 계약정보 탭에서 PO 확인 기능 필요
          break;

        case "EX_FOLLOW_SC_CREATE_REQUIRED":
          // SC 생성 페이지로 이동
          navigate(
            `${EXPORTER_PRIVATE_PATH.CONTRACT_ADD}?id=${params.id}&clientCheck=false`,
          );
          break;

        case "IM_FOLLOW_SC_RECEIVED":
          // SC 확인 필요
          // TODO: 계약정보 탭에서 SC 확인 기능 필요
          break;

        case "IM_FOLLOW_PO_CREATE_REQUIRED":
          // PO 생성 페이지로 이동
          navigate(IMPORTER_PRIVATE_PATH.CONTRACT_ADD);
          break;
      }
    },
    [contractNoticeType, navigate, params.id, getExporterPoHistories],
  );

  // Handle signature requirement from contract viewer modal
  const handleSignatureRequired = useCallback(
    (type: "PO" | "SC") => {
      if (type === "PO") {
        if (isTwoSign) {
          setAlertDialogState(AlertDialogState.SIGNATURE_SELECT_PO);
        } else {
          setAlertDialogState(AlertDialogState.SIGN_PO);
        }
      } else if (type === "SC") {
        if (isTwoSign) {
          setAlertDialogState(AlertDialogState.SIGNATURE_SELECT_SC);
        } else {
          setAlertDialogState(AlertDialogState.CONFIRM_REQUEST_SC);
        }
      }
    },
    [isTwoSign],
  );

  const renderContentByTab = () => {
    switch (tab) {
      case "contractInformation":
        return (
          <ContractInformationTab
            isEditAuth={isEditAuth}
            emailDialogOn={() => setDialogState(DialogState.SEND_EMAIL)}
            historyDialogOn={() => setDialogState(DialogState.CONTRACT_HISTORY)}
            isOnlyReceivedPo={isOnlyReceivedPo}
            isDisabledEdit={isDisabledEdit}
            poNo={poNo}
            onCreatePO={() => navigate(IMPORTER_PRIVATE_PATH.CONTRACT_ADD)}
            onContractNoticeAction={handleContractNoticeAction}
            rejectReason={currentSignature?.rejectReason}
            balanceOfContractV2={balanceOfContractV2}
            percentOfShipment={percentOfShipment}
            totalShippingWeightV2={totalShippingWeightV2}
            scNo={scNo}
            onSignatureRequired={handleSignatureRequired}
            onOpenSignatureViewer={async (type) => {
              try {
                const contractId = Number(params.id);
                let pdfUrl = "";

                if (type === "SC") {
                  const scHistories = await getExporterScHistories({
                    contractId,
                  }).unwrap();
                  pdfUrl = scHistories?.rows?.[0]?.mediaFileUrl;
                } else if (type === "PO") {
                  const poHistories = await getExporterPoHistories({
                    contractId,
                  }).unwrap();
                  pdfUrl = poHistories?.rows?.[0]?.mediaFileUrl;
                }

                if (pdfUrl) {
                  setPdfUrl(pdfUrl);
                  setSelectedSignatureId(null);
                  setSignatureType(type);
                  setDialogState(DialogState.SIGNATURE_FULLSCREEN);
                }
              } catch (error) {
                console.error(`Failed to fetch ${type} PDF URL:`, error);
              }
            }}
          />
        );
      case "processing":
        return (
          <ProcessingTab
            tab={processingTab}
            onTabChange={setProcessingTab}
            signatureStatus={signatureStatus}
            hasLinkedBefore={!!hasLinkedBefore}
          />
        );
      case "files":
        return <ContractFilesTab tab={filesTab} onTabChange={setFilesTab} />;
    }
  };

  const renderContractDetailContent = () => {
    if (isContractUnstable || isMainCategoryUnauthorized) {
      return;
    }

    return (
      <>
        {/* Content */}
        <FlexColumn>
          {/* Header */}
          <HeaderContainer>
            <HeaderButtonContainer>
              {renderContractStatusBadge({
                t,
                type: contractStatus as ContractStatusType | null,
                size: "L",
              })}
            </HeaderButtonContainer>
          </HeaderContainer>

          <Tabs role="tablist">
            {TAB_LIST.map(({ id, label }) => {
              return (
                <StyledTabItem
                  key={id}
                  tabIndex={id === tab ? 0 : -1}
                  isSelect={id === tab}
                  tabValue={id}
                  onClick={() => handleTabChange(id)}
                  onFocusItem={(value) => handleTabChange(value as TabType)}
                >
                  <Typo typoType="b5m" color={id === tab ? "white" : "gray6"}>
                    {t(label)}
                  </Typo>
                </StyledTabItem>
              );
            })}
          </Tabs>
          {renderContentByTab()}
          {/* Last Editor */}
          <EditorContainer>
            <figure>
              <Icon iconSrc={DotGray7Svg} iconSize={4} />
              <Typo color="gray6" typoType="b9r">
                {`${t("contract:exporter.detail.editor.registratant")} (${t(
                  "contract:exporter.detail.editor.registratantAt",
                )}) ${contractWriterName}`}
              </Typo>
              <Typo color="gray7" typoType="b9r">
                {contractCreatedDate}
              </Typo>
            </figure>
            <figure>
              <Icon iconSrc={DotGray4Svg} iconSize={4} />
              <Typo color="gray4" typoType="b9r">
                {`${t("contract:exporter.detail.editor.lastEditor")} (${t(
                  "contract:exporter.detail.editor.lastEditorAt",
                )}) ${contractLastEditorName}`}
              </Typo>
              <Typo color="gray7" typoType="b9r">
                {contractUpdatedDate}
              </Typo>
            </figure>
          </EditorContainer>
        </FlexColumn>

        <BottomContainer>
          <BottomButtonContainer>{renderFooter()}</BottomButtonContainer>
        </BottomContainer>
      </>
    );
  };

  useEffect(() => {
    const fetchContractDetail = async (retryCount = 0) => {
      try {
        await getContractDetail({
          id: Number(params.id),
        }).unwrap();

        // TODO: 계약이 연결되지 않은 경우 SC 탭으로 전환하는 기능 필요
      } catch (e: any) {
        const message = Array.isArray(e.data.message)
          ? e.data.message[0]
          : e.data.message;
        if (e.data.statusCode === 404) {
          alert.showAlert({ type: "error", message });
          navigate(EXPORTER_PRIVATE_PATH.CONTRACT);
          return;
        }

        if (retryCount < 5) {
          handleContentLoadingOn();
          setTimeout(() => {
            fetchContractDetail(retryCount + 1);
          }, 1000);
        } else {
          handleContentLoadingOff();
          alert.showAlert({ type: "error", message });
        }
      }
    };

    fetchContractDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getContractDetail, params.id]);

  useEffect(() => {
    (async () => {
      const poResult = await getExporterPoHistories({
        contractId: Number(params.id),
      });
      const scResult = await getExporterScHistories({
        contractId: Number(params.id),
      });

      await getContractSignatureHistories({ id: Number(params.id) });

      // TODO: PO 조회 오류 시 SC 탭으로 전환하는 기능 필요

      if (!poVersion) {
        setPoVersion?.(poResult.data?.rows?.[0].id);
      }
      if (!scVersion) {
        setScVersion?.(scResult.data?.rows?.[0].id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);
  // URL에 selectTab이 없으면 기본값 설정
  useEffect(() => {
    if (!searchParams.get("selectTab")) {
      setSearchParams({ selectTab: "contractInformation" }, { replace: true });
      setTab("contractInformation");
    }
  }, [searchParams, setSearchParams]);

  // PDF URL 가져오기
  useEffect(() => {
    const fetchPdfUrl = async () => {
      if (dialogState === DialogState.SIGNATURE_FULLSCREEN && params.id) {
        try {
          const contractId = Number(params.id);
          if (signatureType === "PO") {
            const poHistories = await getExporterPoHistories({
              contractId,
            }).unwrap();
            if (poHistories?.rows?.[0]?.mediaFileUrl) {
              setPdfUrl(poHistories.rows[0].mediaFileUrl);
            }
          } else if (signatureType === "SC") {
            const scHistories = await getExporterScHistories({
              contractId,
            }).unwrap();
            if (scHistories?.rows?.[0]?.mediaFileUrl) {
              setPdfUrl(scHistories.rows[0].mediaFileUrl);
            }
          }
        } catch (error) {
          console.error("Failed to fetch PDF URL:", error);
        }
      }
    };

    fetchPdfUrl();
  }, [
    dialogState,
    signatureType,
    params.id,
    getExporterPoHistories,
    getExporterScHistories,
  ]);

  return (
    <ExporterMainLayout
      breadcrumb={
        isContractStable && isMainCategoryAuthorized
          ? [t("sideNav:contract"), t("sideNav:contractDetail")]
          : []
      }
      pageTitle={
        <>
          <PageTitleContainer>
            {isContractStable &&
              isMainCategoryAuthorized &&
              `${t("contract:exporter.detail.header.title")} ${scNo ?? "-"}`}
            <HeaderButtonContainer>
              <BookMarkStatus
                bookMarkStatus={isBookmarked}
                onClickBookMark={handleBookmarkClick}
              />
              <EmailDropdown
                disabled={!isEditAuth || isZeroSc}
                onEmailClick={() => setDialogState(DialogState.SEND_EMAIL)}
                onHistoryClick={() => setDialogState(DialogState.SENT_HISTORY)}
                buttonText={t("contract:exporter.detail.button.emailButton")}
                sendEmailText={t(
                  "contract:exporter.detail.button.sendEmailButton",
                )}
                sentHistoryText={t(
                  "contract:exporter.detail.button.sentHistoryButton",
                )}
              />
            </HeaderButtonContainer>
          </PageTitleContainer>
          {isMainCategoryAuthorized && <ClientName>{clientName}</ClientName>}
        </>
      }
    >
      {/* Contents */}
      {renderContractDetailContent()}
      {renderUnauthorizedDescription()}
      {/* Dialogs , AlertDialogs*/}
      {renderDialogs()}
      {renderAlertDialogs()}
      {/* 서명 등록 다이얼로그 - 독립적으로 렌더링 */}
      {showRegisterSignDialog && (
        <RegisterSignDialog
          id={Number(params.id)}
          onClose={() => setShowRegisterSignDialog(false)}
          signatureType={signatureType || "PO"}
          onSuccess={() => {
            // 서명 등록 성공 시 처리
            alert.showAlert({
              type: "success",
              message: t(
                "contract:exporter.detail.alert.signatureCompletedAlert",
              ),
            });
            // 서명 등록 모달만 닫기
            setShowRegisterSignDialog(false);
            // PDF 뷰어가 열려있는 경우 해당 처리만 수행
            if (dialogState === DialogState.SIGNATURE_FULLSCREEN) {
              // 풀스크린 뷰어 닫기
              setDialogState(DialogState.NULL);
              setPdfUrl(null);
              setSelectedSignatureId(null);
              setSignatureType(null);
            }
            // 계약 상세 정보 다시 가져오기
            getContractDetail({
              id: Number(params.id),
            });
          }}
        />
      )}
    </ExporterMainLayout>
  );
};

export default ExporterContractDetailPage;
